import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, fmtDate } from "@/components/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, MapPin, User, CalendarDays, Dog, PlayCircle, CheckCircle2, RotateCcw, Save, Lightbulb } from "lucide-react";
import { toast } from "sonner";

const SEV_STYLE = { low: "bg-gray-100 text-gray-700", medium: "bg-amber-100 text-amber-800", high: "bg-orange-100 text-orange-800", critical: "bg-red-100 text-red-800" };

export default function QualityIssueDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = ["super_admin", "head_office_admin", "franchise_owner", "location_manager"].includes(user?.role);
  const [issue, setIssue] = useState(null);
  const [err, setErr] = useState(false);
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    try { const r = await api.get(`/quality/issues/${id}`); setIssue(r.data); setNotes(r.data.internal_notes || ""); }
    catch { setErr(true); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => { try { await api.patch(`/quality/issues/${id}`, { status }); toast.success("Updated"); await load(); } catch { toast.error("Failed"); } };
  const saveNotes = async () => { try { await api.patch(`/quality/issues/${id}`, { internal_notes: notes }); toast.success("Notes saved"); await load(); } catch { toast.error("Failed"); } };

  if (err) return <div className="text-center py-20 text-muted-foreground">Issue not found.</div>;
  if (!issue) return <div className="text-center py-20 text-muted-foreground">Loading...</div>;

  const related = [
    issue.location_name && { icon: MapPin, label: "Location", value: issue.location_name },
    issue.trainer_name && { icon: User, label: "Trainer", value: issue.trainer_name },
    issue.class_name && { icon: CalendarDays, label: "Class", value: issue.class_name },
    issue.dog_name && { icon: Dog, label: "Dog", value: issue.dog_name },
  ].filter(Boolean);

  return (
    <div>
      <Button variant="ghost" size="sm" className="gap-2 mb-4 -ml-2 text-muted-foreground" onClick={() => navigate("/quality")} data-testid="issue-back"><ArrowLeft size={16} /> Back to HV Quality</Button>

      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${SEV_STYLE[issue.severity]}`}>{issue.severity}</span>
            <StatusBadge value={issue.status} />
          </div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">{issue.title}</h1>
          <div className="text-sm text-muted-foreground capitalize mt-1">{issue.type.replace(/_/g, " ")} · Created {fmtDate(issue.created_at)} by {issue.created_by}</div>
        </div>
        {canManage && (
          <div className="flex gap-2">
            {issue.status !== "in_progress" && issue.status !== "resolved" && <Button variant="outline" className="gap-1.5" onClick={() => setStatus("in_progress")} data-testid="issue-in-progress"><PlayCircle size={15} /> In progress</Button>}
            {issue.status !== "resolved" ? <Button className="gap-1.5" onClick={() => setStatus("resolved")} data-testid="issue-resolve"><CheckCircle2 size={15} /> Resolve</Button>
              : <Button variant="outline" className="gap-1.5" onClick={() => setStatus("open")} data-testid="issue-reopen"><RotateCcw size={15} /> Reopen</Button>}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {issue.description && <Card className="border-border"><CardContent className="p-5"><div className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground">Description</div><p className="text-sm mt-1.5">{issue.description}</p></CardContent></Card>}
          {issue.recommended_action && (
            <Card className="border-border bg-secondary/40"><CardContent className="p-5">
              <div className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground flex items-center gap-1.5"><Lightbulb size={13} /> Recommended Action</div>
              <p className="text-sm mt-1.5">{issue.recommended_action}</p>
            </CardContent></Card>
          )}
          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-base">Internal Notes</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add internal notes (not visible to clients)..." rows={4} disabled={!canManage} data-testid="issue-notes" />
              {canManage && <Button size="sm" className="gap-1.5" onClick={saveNotes} data-testid="issue-notes-save"><Save size={14} /> Save notes</Button>}
              <div className="pt-3 border-t border-border/60"><div className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-1">Activity Log</div><p className="text-xs text-muted-foreground">Full activity history is coming soon. {issue.resolved_at && `Resolved ${fmtDate(issue.resolved_at)}.`}</p></div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-base">Related</CardTitle></CardHeader>
            <CardContent className="space-y-2.5">
              {related.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-sm"><r.icon size={14} className="text-muted-foreground" /><span className="text-muted-foreground">{r.label}:</span> <span className="font-medium">{r.value}</span></div>
              ))}
              {related.length === 0 && <p className="text-sm text-muted-foreground">No related records.</p>}
              {issue.due_date && <div className="flex items-center gap-2 text-sm pt-1"><CalendarDays size={14} className="text-muted-foreground" /><span className="text-muted-foreground">Due:</span> {fmtDate(issue.due_date)}</div>}
              {issue.assigned_to_name && <div className="flex items-center gap-2 text-sm"><User size={14} className="text-muted-foreground" /><span className="text-muted-foreground">Assigned:</span> {issue.assigned_to_name}</div>}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
